import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import MtfReplayPanel from "./MtfReplayPanel";

/**
 * MTF Momentum Lab — observatory for the NIFTY_MTF_IMPULSE_CONTINUATION_V1
 * paper experiment. Data-only surface (no inputs): source-signal table with
 * per-variant verdicts, isolated book state, experiment lifecycle controls,
 * status cards.
 *
 * Config tuning happens on /algorithm (MTF Impulse Config panel). Chart with
 * regime shading is deferred to a follow-up (needs recharts + candle data
 * plumbing which is heavier than this observatory).
 */
export default function MtfLab() {
  const [signals, setSignals] = useState([]);
  const [books, setBooks] = useState(null);
  const [experiment, setExperiment] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [regime, setRegime] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [sRes, bRes, eRes, cRes, lRes, rRes] = await Promise.all([
        api.get("/mtf/signals?limit=30"),
        api.get("/mtf/books"),
        api.get("/mtf/experiment"),
        api.get("/mtf/config"),
        api.get("/mtf/live-status").catch(() => ({ data: null })),
        api.get("/mtf/regime-today").catch(() => ({ data: null })),
      ]);
      setSignals(sRes.data.items || []);
      setBooks(bRes.data);
      setExperiment(eRes.data);
      setCfg(cRes.data);
      setLiveStatus(lRes.data);
      setRegime(rRes.data);
    } catch (e) {
      setMsg({ level: "error", text: e?.response?.data?.detail || e.message });
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const experimentAction = async (action) => {
    setBusy(true); setMsg(null);
    try {
      await api.post("/mtf/experiment", { action });
      setMsg({ level: "ok", text: `Experiment ${action} → OK` });
      await load();
    } catch (e) {
      setMsg({ level: "error", text: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="mtf-lab-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-purple-400 mb-2">
          MTF Momentum Lab
        </div>
        <h1 className="text-2xl font-light text-slate-100">
          NIFTY 1-min impulse continuation study
        </h1>
        <p className="text-xs text-slate-500 mt-2">
          Long-only paper experiment. Source signals fan out to two isolated
          ₹1,00,000 books — options (nearest-ITM CE) and NIFTYBEES (equity
          intraday). Every rejection is persisted. No live trading, no ML gate,
          no screener boost.
        </p>
      </div>

      {/* Live runtime banner — proves the strategy is actually running
          in parallel with ORB, no manual start required. */}
      <MtfLiveRuntimeCard status={liveStatus} />

      {/* Today's 5m regime strip — one column per completed 5m bar, coloured by regime. */}
      <RegimeStripChart data={regime} />

      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatusCard label="Strategy" value={cfg?.effective?.enabled ? "Enabled" : "Disabled"}
                     tone={cfg?.effective?.enabled ? "pos" : "muted"} />
        <StatusCard label="Mode"
                     value={cfg?.effective?.paper_only === false ? "LIVE ⚠" : "Paper only"}
                     tone="warn" />
        <StatusCard label="Experiment"
                     value={experiment?.state || "—"}
                     sub={experiment?.params_frozen ? "params frozen" : ""}
                     tone={experiment?.state === "ACTIVE" ? "pos" : "muted"} />
        <StatusCard label="Config"
                     value={`v${cfg?.config_version || 0}`}
                     sub={cfg?.config_hash || ""} tone="muted" />
      </div>

      {/* Books */}
      <div className="surface rounded-sm p-4" data-testid="mtf-books">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
          Isolated Paper Books
        </div>
        <p className="text-xs text-amber-400 mb-3">
          {books?.capital_note}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(books?.books || []).map((b) => (
            <div key={b.book_id} className="border border-slate-800 rounded-sm p-3">
              <div className="text-[11px] font-mono text-slate-300 mb-2">{b.book_id}</div>
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                <div><span className="text-slate-500">Initial:</span> ₹{Number(b.initial_capital ?? 100000).toLocaleString("en-IN")}</div>
                <div><span className="text-slate-500">Available:</span> ₹{b.available_cash != null ? Number(b.available_cash).toLocaleString("en-IN") : "—"}</div>
                <div><span className="text-slate-500">Realized P&L:</span> <span className={Number(b.realized_pnl || 0) >= 0 ? "text-pos" : "text-neg"}>₹{Number(b.realized_pnl || 0).toFixed(2)}</span></div>
                <div><span className="text-slate-500">Closed:</span> {b.closed_trades_count || 0}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Experiment controls — collapsed by default with a plain-english
          explainer so operators don't confuse these with a live-strategy
          on/off switch. The live strategy runs automatically during
          market hours (see Live Runtime card above). */}
      <ExperimentPanel experiment={experiment} onAction={experimentAction}
                        busy={busy} msg={msg} />

      {/* Source signals table */}
      <div className="surface rounded-sm p-4" data-testid="mtf-signals">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Recent Source Signals
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            {signals.length} shown
          </div>
        </div>
        {signals.length === 0 ? (
          <p className="text-xs text-slate-500 py-6 text-center">
            No signals yet. Once the live tick stream is active and a bullish
            1m impulse candle passes the regime + volume gates, source signals
            will appear here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead className="text-[9px] uppercase tracking-[0.15em] text-slate-600">
                <tr>
                  <th className="text-left py-1">Time</th>
                  <th className="text-right">NIFTY</th>
                  <th className="text-right">Impulse ATR</th>
                  <th className="text-right">Body%</th>
                  <th className="text-right">Vol Ratio</th>
                  <th className="text-right">ADX</th>
                  <th className="text-right">Trigger</th>
                  <th className="text-right">Stop / Target</th>
                  <th className="text-center">OPT</th>
                  <th className="text-center">BEES</th>
                </tr>
              </thead>
              <tbody>
                {signals.map((s) => {
                  const opt = (s.evaluations || []).find((e) => e.execution_strategy_id === "NIFTY_MTF_IMPULSE_OPTIONS_V1");
                  const bees = (s.evaluations || []).find((e) => e.execution_strategy_id === "NIFTYBEES_MTF_IMPULSE_V1");
                  return (
                    <React.Fragment key={s.source_signal_id}>
                      <tr className="border-t border-slate-800 hover:bg-slate-900/50 cursor-pointer"
                          onClick={() => setExpanded(expanded === s.source_signal_id ? null : s.source_signal_id)}
                          data-testid={`mtf-signal-row-${s.source_signal_id}`}>
                        <td className="py-1.5 text-slate-400">{(s.impulse_candle_id || "").slice(11, 16)}</td>
                        <td className="text-right text-slate-300">{Number(s.nifty_signal_price || 0).toFixed(2)}</td>
                        <td className="text-right">{(s.body_fraction && s.atr_1m) ? ((s.impulse_high - s.impulse_low) / s.atr_1m).toFixed(2) : "—"}</td>
                        <td className="text-right">{Number(s.body_fraction || 0).toFixed(2)}</td>
                        <td className="text-right">{Number(s.niftybees_volume_ratio || 0).toFixed(2)}</td>
                        <td className="text-right">{Number(s.adx_5m || 0).toFixed(1)}</td>
                        <td className="text-right">{Number(s.entry_trigger_price || 0).toFixed(2)}</td>
                        <td className="text-right text-slate-500">{Number(s.source_stop || 0).toFixed(2)} / {Number(s.source_target || 0).toFixed(2)}</td>
                        <td className={`text-center ${opt?.accepted ? "text-pos" : "text-slate-500"}`}>
                          {opt ? (opt.accepted ? "✓" : (opt.rejection_code || "").split("_").slice(-2).join(" ").toLowerCase()) : "—"}
                        </td>
                        <td className={`text-center ${bees?.accepted ? "text-pos" : "text-slate-500"}`}>
                          {bees ? (bees.accepted ? "✓" : (bees.rejection_code || "").split("_").slice(-2).join(" ").toLowerCase()) : "—"}
                        </td>
                      </tr>
                      {expanded === s.source_signal_id && (
                        <tr>
                          <td colSpan={10} className="bg-slate-950 p-3 border-t border-slate-800">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Options branch</div>
                                <pre className="text-[10px] text-slate-300 overflow-x-auto">
{JSON.stringify(opt || {}, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">NIFTYBEES branch</div>
                                <pre className="text-[10px] text-slate-300 overflow-x-auto">
{JSON.stringify(bees || {}, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Historical Replay (Phase D) */}
      <MtfReplayPanel />
    </div>
  );
}

function StatusCard({ label, value, sub, tone = "muted" }) {
  const toneClass = tone === "pos" ? "text-pos" : tone === "warn" ? "text-warn" : "text-slate-300";
  return (
    <div className="surface rounded-sm p-3">
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
      <div className={`text-sm font-mono ${toneClass}`}>{value}</div>
      {sub ? <div className="text-[10px] text-slate-600 font-mono mt-0.5 truncate">{sub}</div> : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Live runtime banner — a single glance tells you whether the MTF strategy
// is actually receiving ticks + building candles + counting signals TODAY,
// in parallel with the ORB algo. No user action required to make it work.
// ---------------------------------------------------------------------------
function MtfLiveRuntimeCard({ status }) {
  const [healBusy, setHealBusy] = React.useState(false);
  const [healMsg, setHealMsg] = React.useState(null);
  const runManualHeal = async () => {
    setHealBusy(true); setHealMsg(null);
    try {
      const r = await api.post("/mtf/ensure-subscribed");
      const parts = [];
      if (r.data.nifty_spot_added) parts.push("NIFTY spot added");
      if (r.data.niftybees_added) parts.push(`NIFTYBEES added (${r.data.niftybees_token})`);
      if (parts.length === 0) parts.push("already subscribed — nothing to fix");
      if (r.data.error) parts.push(r.data.error);
      setHealMsg({ level: r.data.error ? "error" : "ok", text: parts.join(" · ") });
    } catch (e) {
      setHealMsg({ level: "error", text: e?.response?.data?.detail || e.message });
    } finally { setHealBusy(false); }
  };
  if (!status) {
    return (
      <div className="surface rounded-sm p-4" data-testid="mtf-live-runtime-card">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
          Live Runtime
        </div>
        <p className="text-xs text-slate-500">Loading…</p>
      </div>
    );
  }
  const nifty = status.nifty_spot_subscribed;
  const bees = status.niftybees_subscribed;
  const wired = status.running && nifty && bees;
  const bar1mAge = status.last_1m_candle_age_seconds;
  const feed_ok = wired && bar1mAge !== null && bar1mAge < 120;
  let headline;
  let headlineClass;
  if (feed_ok) {
    headline = `RUNNING · ${status.source_signals_today} signals · ${status.open_positions} open · last 1m bar ${bar1mAge}s ago`;
    headlineClass = "text-pos";
  } else if (wired && bar1mAge === null) {
    headline = "SUBSCRIBED · waiting for first 1m bar to complete";
    headlineClass = "text-warn";
  } else if (status.running && (!nifty || !bees)) {
    headline = "FEED UP · but NIFTY spot / NIFTYBEES not subscribed yet — restart the live feed";
    headlineClass = "text-warn";
  } else {
    headline = "IDLE · live feed is not running (outside market hours or Zerodha disconnected)";
    headlineClass = "text-slate-400";
  }
  return (
    <div className="surface rounded-sm p-4" data-testid="mtf-live-runtime-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Live Runtime — parallel to ORB, always-on during market hours
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runManualHeal} disabled={healBusy || !status.running}
                   data-testid="mtf-fix-subscriptions-btn"
                   className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm bg-slate-800 hover:bg-purple-700 disabled:opacity-30 text-slate-200"
                   title="Force-subscribe NIFTY spot + NIFTYBEES on the running feed. Idempotent, safe to click multiple times.">
            Fix subscriptions
          </button>
          <div className={`text-[11px] font-mono ${headlineClass}`}>{headline}</div>
        </div>
      </div>
      {healMsg ? (
        <div className={`text-[10px] font-mono mb-2 ${healMsg.level === "ok" ? "text-pos" : "text-red-400"}`}
             data-testid="mtf-fix-subscriptions-result">
          {healMsg.text}
        </div>
      ) : null}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
        <RuntimeField label="NIFTY spot" ok={nifty} value={nifty ? "subscribed" : "missing"} />
        <RuntimeField label="NIFTYBEES" ok={bees}
                       value={bees ? `subscribed (${status.niftybees_token})` : "missing"} />
        <RuntimeField label="Last 1m bar"
                       value={status.last_1m_candle_ts ? `${(status.last_1m_close || 0).toFixed(2)} · ${bar1mAge}s ago` : "none yet"}
                       ok={bar1mAge !== null && bar1mAge < 120} />
        <RuntimeField label="Last 5m bar"
                       value={status.last_5m_candle_ts
                               ? `${(status.last_5m_candle_ts || "").slice(11, 16)} IST`
                               : "none yet"} />
        <RuntimeField label="Signals today" value={String(status.source_signals_today)} />
        <RuntimeField label="Rejections today" value={String(status.candidate_rejections_today)} />
        <RuntimeField label="Open positions" value={String(status.open_positions)}
                       ok={status.open_positions > 0} />
        <RuntimeField label="Config" value={`v${status.config_version ?? 0}`}
                       sub={status.config_hash || ""} />
      </div>
    </div>
  );
}

function RuntimeField({ label, value, sub, ok }) {
  let cls = "text-slate-300";
  if (ok === true) cls = "text-pos";
  else if (ok === false) cls = "text-warn";
  return (
    <div className="border-l-2 border-slate-800 pl-2">
      <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={cls}>{value}</div>
      {sub ? <div className="text-[9px] text-slate-600 truncate">{sub}</div> : null}
    </div>
  );
}



// ---------------------------------------------------------------------------
// Today's 5m regime strip — a single-row heat strip showing which minutes
// the strategy considered BULLISH (green), NEUTRAL (grey), or BEARISH (red).
// Explains "why aren't signals firing" at a glance: if the strip is grey
// or red, the regime gate is rejecting every candidate bar and the impulse
// evaluator never even runs.
// ---------------------------------------------------------------------------
const REGIME_COLORS = {
  BULLISH: "#22c55e",
  NEUTRAL: "#64748b",
  BEARISH: "#ef4444",
  WARMUP: "#334155",
};

function RegimeStripChart({ data }) {
  if (!data) {
    return (
      <div className="surface rounded-sm p-4" data-testid="mtf-regime-strip">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
          Today&apos;s 5m Regime
        </div>
        <p className="text-xs text-slate-500">Loading…</p>
      </div>
    );
  }
  const bars = data.regime_bars || [];
  if (bars.length === 0) {
    return (
      <div className="surface rounded-sm p-4" data-testid="mtf-regime-strip">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
          Today&apos;s 5m Regime
        </div>
        <p className="text-xs text-slate-500">
          {data.reason || "No 5m bars completed today yet."}
        </p>
      </div>
    );
  }
  const counts = bars.reduce((acc, b) => {
    acc[b.regime] = (acc[b.regime] || 0) + 1;
    return acc;
  }, {});
  const bull = counts.BULLISH || 0;
  const bear = counts.BEARISH || 0;
  const neut = counts.NEUTRAL || 0;
  const warm = counts.WARMUP || 0;
  return (
    <div className="surface rounded-sm p-4" data-testid="mtf-regime-strip">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Today&apos;s 5m Regime · one column per 5m bar
        </div>
        <div className="text-[10px] font-mono text-slate-500">
          <span className="text-pos">bull {bull}</span> ·{" "}
          <span className="text-slate-400">neutral {neut}</span> ·{" "}
          <span className="text-red-400">bear {bear}</span>
          {warm > 0 ? <> · <span className="text-slate-600">warmup {warm}</span></> : null}
        </div>
      </div>
      {/* Strip: fixed-height coloured cells. One per completed 5m bar. */}
      <div className="flex h-8 rounded-sm overflow-hidden border border-slate-800"
           data-testid="mtf-regime-strip-cells">
        {bars.map((b) => (
          <div
            key={b.ts}
            title={`${b.hhmm} · ${b.regime}${b.rejection_code ? ` · ${b.rejection_code}` : ""} · close ${b.close?.toFixed?.(2)}`}
            className="flex-1 border-r border-slate-950/40 last:border-r-0"
            style={{ backgroundColor: REGIME_COLORS[b.regime] || "#334155" }}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500">
        <LegendSwatch color={REGIME_COLORS.BULLISH} label="Bullish — every gate passes; impulse evaluator runs" />
        <LegendSwatch color={REGIME_COLORS.NEUTRAL} label="Neutral — trend up but ADX / VWAP fails" />
        <LegendSwatch color={REGIME_COLORS.BEARISH} label="Bearish — EMA fast ≤ EMA slow; no long signals" />
      </div>
      <div className="mt-1 text-[10px] text-slate-600">
        First bar: {bars[0]?.hhmm} · Last bar: {bars[bars.length - 1]?.hhmm} IST
      </div>
    </div>
  );
}

function LegendSwatch({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Experiment panel — the four Start/Pause/Resume/End buttons are for a
// FORMAL RESEARCH EXPERIMENT (60-day locked-config window). They do NOT
// control live execution. Kept but tucked behind a collapse + explainer.
// ---------------------------------------------------------------------------
function ExperimentPanel({ experiment, onAction, busy, msg }) {
  const [open, setOpen] = useState(false);
  const state = experiment?.state || "NOT_STARTED";
  return (
    <div className="surface rounded-sm p-4" data-testid="mtf-experiment">
      <button onClick={() => setOpen((v) => !v)}
              data-testid="mtf-experiment-toggle"
              className="w-full flex items-center justify-between text-left">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Formal Research Experiment (Advanced)
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            These buttons control a 60-day locked-config research window —
            NOT the live strategy. The strategy runs automatically during
            market hours regardless of experiment state.
          </div>
        </div>
        <div className="text-[10px] font-mono text-slate-400 shrink-0 ml-3">
          {state}{experiment?.params_frozen ? " · frozen" : ""} · {open ? "hide ▲" : "show ▼"}
        </div>
      </button>
      {open ? (
        <div className="mt-4 border-t border-slate-800 pt-4">
          <p className="text-xs text-slate-400 mb-3">
            Research criteria to pass: profit factor ≥ 1.20 after costs ·
            expectancy ≥ +0.10R · max drawdown ≤ 8R · positive in ≥2 of 3
            subperiods · no single day &gt; 30% of total profit.
            Minimum sample: 60 trading days AND 200 candidate signals.
          </p>
          <div className="flex flex-wrap gap-2">
            {["start", "pause", "resume", "end"].map((a) => (
              <button key={a} onClick={() => onAction(a)} disabled={busy}
                      data-testid={`mtf-experiment-${a}`}
                      className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm bg-slate-800 hover:bg-purple-700 disabled:opacity-40 text-slate-200">
                {a}
              </button>
            ))}
          </div>
          {msg ? (
            <div className={`text-xs font-mono mt-3 ${msg.level === "ok" ? "text-pos" : "text-red-400"}`}>
              {msg.text}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

